import React, { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileUp, Loader2, Check, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const categories = ["Aptitude", "Reasoning", "Technical", "Verbal", "Versant", "AML & CTF", "Interview Prep", "Medical Billing", "AR Calling", "SAP MM"];
const difficulties = ["easy", "medium", "hard", "advanced"];

export default function ImportCourse() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [form, setForm] = useState({ title: "", category: "Technical", difficulty: "medium", description: "" });
  const [fileName, setFileName] = useState("");
  const [htmlText, setHtmlText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Enter a course title."); return; }
    const file = fileRef.current?.files?.[0];
    if (!file && !htmlText.trim()) { toast.error("Upload an .html file or paste HTML."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("difficulty", form.difficulty);
      fd.append("description", form.description);
      if (file) fd.append("file", file);
      if (htmlText.trim()) fd.append("html_text", htmlText);
      const { data } = await api.post("/admin/courses/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.course);
      qc.invalidateQueries({ queryKey: ["courses"] });
      toast.success("Course imported!");
      setHtmlText("");
      setFileName("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      toast.error("Import failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="import-course-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin Tools</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <FileUp className="w-8 h-8 text-primary" /> Import Course
        </h1>
        <p className="text-muted-foreground mt-1">Upload an .html file or paste HTML — it's converted to a clean lesson and added as a course.</p>
      </div>

      <div className="p-6 rounded-md border border-border bg-card space-y-4">
        <div>
          <Label htmlFor="title">Course title</Label>
          <Input id="title" data-testid="import-title-input" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1.5 rounded-md" placeholder="e.g. SAP FICO Crash Course" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5 rounded-md" data-testid="import-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Difficulty</Label>
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="mt-1.5 rounded-md" data-testid="import-difficulty-select"><SelectValue /></SelectTrigger>
              <SelectContent>{difficulties.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="desc">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input id="desc" data-testid="import-desc-input" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1.5 rounded-md" placeholder="Auto-generated from content if left blank" />
        </div>

        <div>
          <Label>HTML file</Label>
          <label className="mt-1.5 flex items-center gap-3 p-4 rounded-md border border-dashed border-border bg-background cursor-pointer hover:bg-secondary transition-colors" data-testid="import-file-dropzone">
            <UploadCloud className="w-5 h-5 text-primary" />
            <span className="text-sm text-muted-foreground">{fileName || "Choose an .html file to upload"}</span>
            <input
              ref={fileRef}
              type="file"
              accept=".html,.htm,text/html"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
            />
          </label>
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 my-1">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-mono uppercase text-muted-foreground">or paste HTML</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Textarea
            data-testid="import-html-textarea"
            value={htmlText}
            onChange={(e) => setHtmlText(e.target.value)}
            className="rounded-md font-mono text-xs h-32"
            placeholder="<h1>My Course</h1><p>...</p>"
          />
        </div>

        {error && <p className="text-sm text-destructive" data-testid="import-error">{error}</p>}

        <Button onClick={submit} disabled={loading} className="rounded-md gap-2 h-11" data-testid="import-submit-button">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</> : <><FileUp className="w-4 h-4" /> Import Course</>}
        </Button>
      </div>

      {result && (
        <div className="p-6 rounded-md border border-success/40 bg-card fade-in-up" data-testid="import-result">
          <div className="flex items-center gap-2 mb-3 text-success">
            <Check className="w-5 h-5" />
            <span className="font-heading font-bold">Course imported!</span>
          </div>
          <h3 className="font-heading font-bold text-xl mb-1">{result.title}</h3>
          <p className="text-sm text-muted-foreground mb-4">{result.category} · {result.difficulty} · {result.content.length} chars</p>
          <Link to={`/courses/${result.id}`}>
            <Button variant="outline" className="rounded-md" data-testid="view-imported-course">View Course</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
